import React, { useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { CustomField, JsonConfig } from '../../../types/configTemplate';

/*
 * The values a template sets, read-only, in four groups:
 *   Syslog, MQTT, General  — the known keys, in a fixed order
 *   Other                  — any other key of the json_config
 *   Custom fields          — the template's custom_fields entries
 * Passwords are masked; ANEP admins can reveal them.
 */

type GroupId = 'syslog' | 'mqtt' | 'general' | 'other' | 'custom';

const KNOWN_GROUPS: Array<{ id: GroupId; keys: string[] }> = [
  { id: 'syslog', keys: ['syslog_host', 'syslog_port', 'syslog_facility'] },
  { id: 'mqtt', keys: ['mqtt_host', 'mqtt_port', 'mqtt_user', 'mqtt_pass'] },
  { id: 'general', keys: ['provisioning', 'keepalive_timer', 'password', 'generate_password'] },
];

const SECRET_KEYS = new Set(['password', 'mqtt_pass']);
const MASK = '••••••••';

interface ConfigRow {
  key: string;
  value: unknown;
  /** The kind shown as a chip, guessed from the key and the value. */
  kind: string;
  secret: boolean;
}

interface ConfigGroup {
  id: GroupId;
  rows: ConfigRow[];
}

const kindOf = (key: string, value: unknown): string => {
  if (key.includes('port')) return 'port';
  if (key.includes('host')) return 'hostname';
  if (key === 'provisioning') return 'url';
  if (SECRET_KEYS.has(key)) return 'password';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'object' && value !== null) return 'json';
  return 'string';
};

const row = (key: string, value: unknown): ConfigRow => ({ key, value, kind: kindOf(key, value), secret: SECRET_KEYS.has(key) });

/** The template's values arranged in groups; empty groups are left out. */
export const groupConfigRows = (config: JsonConfig, customFields: CustomField[]): ConfigGroup[] => {
  const knownKeys = new Set(KNOWN_GROUPS.flatMap((g) => g.keys));
  const groups: ConfigGroup[] = KNOWN_GROUPS.map((g) => ({
    id: g.id,
    rows: g.keys.filter((key) => config[key] !== undefined).map((key) => row(key, config[key])),
  }));
  const otherKeys = Object.keys(config).filter((key) => key !== 'custom_fields' && !knownKeys.has(key));
  groups.push({ id: 'other', rows: otherKeys.map((key) => row(key, config[key])) });
  groups.push({ id: 'custom', rows: customFields.map((field) => row(field.name, field.value)) });
  return groups.filter((g) => g.rows.length > 0);
};

const EyeIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {open ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

/** A value as text: "random (12 characters)" for a generated password, JSON for objects, "—" when empty. */
const useValueText = () => {
  const { t } = useI18n();
  return (r: ConfigRow): string => {
    if (r.key === 'generate_password' && r.value === true) return t('configTemplates.detail.randomValue');
    if (r.value === null || r.value === undefined) return '—';
    if (typeof r.value === 'object') return JSON.stringify(r.value);
    return String(r.value) || '—';
  };
};

/** A secret value: masked, with an eye to reveal it when the caller is allowed to. */
const SecretValue: React.FC<{ text: string; canReveal: boolean; revealed: boolean; onToggle: () => void }> = ({ text, canReveal, revealed, onToggle }) => {
  const { t } = useI18n();
  const label = revealed ? t('configTemplates.detail.hide') : t('configTemplates.detail.reveal');
  return (
    <span className="tpd-secret">
      <span className="monospace">{canReveal && revealed ? text : MASK}</span>
      {canReveal && (
        <button type="button" className="btn-icon tpd-eye" onClick={onToggle} title={label} aria-label={label}>
          <EyeIcon open={revealed} />
        </button>
      )}
    </span>
  );
};

interface ConfigFieldsTableProps {
  jsonConfig?: JsonConfig | null;
  customFields: CustomField[];
  canRevealPassword?: boolean;
}

export const ConfigFieldsTable: React.FC<ConfigFieldsTableProps> = ({ jsonConfig, customFields, canRevealPassword = false }) => {
  const { t } = useI18n();
  const valueText = useValueText();
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const toggleReveal = (key: string) =>
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const groups = groupConfigRows(jsonConfig || {}, customFields);
  if (groups.length === 0) {
    return <div className="tpd-empty"><p>{t('configTemplates.detail.noConfig')}</p></div>;
  }

  return (
    <div className="field-defs-list tpd-groups">
      {groups.map((group) => (
        <div key={group.id} className="field-group tpd-group">
          <h4 className="group-header tpd-group-title">
            {t(`configTemplates.groups.${group.id}`)}
            <span className="ud-tab-count">{group.rows.length}</span>
          </h4>
          <table className="field-defs-table tpd-table">
            <thead>
              <tr>
                <th>{t('devices.fieldName')}</th>
                <th>{t('common.type')}</th>
                <th>{t('common.value')}</th>
              </tr>
            </thead>
            <tbody>
              {group.rows.map((r) => (
                <tr key={r.key}>
                  <td><code>{r.key}</code></td>
                  <td><span className="type-badge tpd-type" data-kind={r.kind}>{r.kind}</span></td>
                  <td>
                    {r.secret && r.value
                      ? <SecretValue text={valueText(r)} canReveal={canRevealPassword} revealed={revealed.has(r.key)} onToggle={() => toggleReveal(r.key)} />
                      : <span className="monospace">{valueText(r)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};
